import { isC3Experience } from "../../../shared/util/C3Utils";
define((require) => {
    "use strict";

    const AddVariantAction      = require("ui/actions/AddVariantAction");
    const BreakpointGroup       = require("shared/ui/breakpoint/BreakpointGroup");
    const EditorEvents          = require("shared/services/EditorEvents");
    const LoggedInUserState     = require("shared/ui/user/LoggedInUserState");
    const Promise               = require("shared/util/Promise");
    const SyncManager           = require("shared/services/sync/SyncManager");
    const SharedEntityType      = require("ui/experienceOverview/experiencePermissions/SharedEntityType");
    const SharedEntityHelper    = require("ui/experienceOverview/experiencePermissions/SharedEntityHelper");
    const Configuration = require("shared/services/Configuration");

    /**
     * An action that either creates a new breakpoint group or adds an new variant to the existing group.
     *
     * @class AddBreakpointAction
     *
     */
    return AddVariantAction.extend({

        _performExecution() {
            const experience = this._experience;
            const isEmailModeEnabled = experience.attributes.emailModeEnabled;
            if (isEmailModeEnabled) return;
            const isC3Exp = isC3Experience();

            let promise = Promise.resolved();
            const groupAlreadyExists = this._group;
            // PE-1504: Fix behavior duplication in Create Variant action
            const clone = experience.clone({duplicate: true, duplicateExperienceWithUUID: true});

            clone.set("name", `${experience.get("name")} (new)`, {silent: true});
            // Create new group if not already a group
            if (!groupAlreadyExists && !isC3Exp) {
                const lowerBounds = LoggedInUserState.get("companySettings").get("breakpoints").getLowerBounds();
                const promises = [];

                this._group = new BreakpointGroup({name: `${experience.get("name")} group`});

                // Ensure we only get one save by blocking the queue until all assets added
                const unblock = SyncManager.block(this._group);

                // Copy all breakpoints to the new group and set the first variant as the default asset
                lowerBounds.forEach((breakpoint) => {
                    promises.push(this._group.attach(experience, breakpoint.clone()));
                });

                unblock();

                // When group is saved, save the clone and attach it
                promise = Promise.all(promises);
            }

            return promise
                .then(() => clone.save())
                .then(() => {
                    // If the group was just created AND we're inside the project
                    // context, attach the group to the project and detach the experience.
                    if (!groupAlreadyExists && !isC3Exp) {
                        this._trackVariant(EditorEvents.constants.CREATE_GROUP, {
                            experience: clone,
                            triggerOrigin: "CreateBreakpointGroupDropdown"
                        });
                        if (this._project) {
                            return this._project
                                // Fetch to ensure project model is in sync (avoiding potential version conflicts).
                                .fetch()
                                .then(() => Promise.all([this._project.detach(experience), this._project.attach(this._group)]));
                        }
                    }
                })
                .then(() =>{
                    if(isC3Exp){
                        const newExperienceId = clone.get("id");
                        const currentLanguage = this._experience.get("language");
                        const { experiences } = this._collections;
                        const currentLanguageExperience = experiences.filter((exp) => exp.language === currentLanguage);

                        // This will execute for first time when user create device variant
                        if (currentLanguageExperience.length === 1){
                            const mappedLangEperienceId = this._collections.experiences.reduce((acc, exp) => {
                                acc[exp.language] = exp.experienceId
                                return acc;
                            }, {});
                            // we can also use "0" as existing threshold as it's default threshold for experiences when created from C3-Dashboard
                            const existingThreshold = this._collections.experiences[0].threshold; 
                            const allExpLanguages = experiences.map(exp => exp.language);
                            const uniqueLanguages = [...new Set(allExpLanguages)];
0
                            const thresholdToAssign = LoggedInUserState.get("companySettings").get("breakpoints")
                                .getLowerBounds()
                                .map((breakpoint) => {
                                    const threshold = breakpoint.get("threshold")
                                    if (existingThreshold !== threshold){
                                        return threshold;
                                    }
                                })
                                .filter(threshold => threshold);
                            
                            const newExpCollectionToAdd = [];
                            uniqueLanguages.forEach(language => {
                                const experienceId = mappedLangEperienceId[language]
                                thresholdToAssign.forEach((threshold) =>{
                                    newExpCollectionToAdd.push({
                                        experienceId,
                                        threshold,
                                        language
                                    });
                                });
                            });
                            this._collections.experiences.push(...newExpCollectionToAdd);
                        }
                        const experienceDetails = {
                            "experienceId": newExperienceId,
                            "threshold": null,
                            "language": clone.get("language")
                        };
                        this._collections.experiences.push(experienceDetails);

                        const expCollectionId = this._collections.id,
                            endpointUrl = `${Configuration.c3ApibaseUrl}/v1/experienceCollections/${expCollectionId}`,
                            token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token"),
                            bearerToken = `Bearer ${token}`;

                        const options = {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": bearerToken
                            },
                            body: JSON.stringify(this._collections),
                            method: "PUT",
                        };

                        fetch(endpointUrl, options)

                    } else this._group.attach(clone).then(() => {
                        // We can add variants only to existing groups.
                        if (groupAlreadyExists) {
                            this._trackVariant(EditorEvents.constants.CREATE_VARIANT, {
                                experience: clone,
                                triggerOrigin: "AddBreakpointVariantPlusIcon"
                            });
                        }
                    })})
                .then(() => {
                    if(isC3Exp) return;
                    SharedEntityHelper.movePermissions({
                        sourceID: this._experience.get("id"),
                        sourceType: SharedEntityType.EXPERIENCE,
                        companyId: this._experience.get("companyId"),
                        destID: this._group.get("id"),
                        destType: SharedEntityType.GROUP
                    });
                })
                .then(() =>
                    // Refresh the cloned experience to stay in sync with the server.
                    clone.fetch());
        },


        /**
         * Sends Mix Panel event for the variant.
         *
         * @param {String} eventName The name of the event.
         * @param {Object} payload Object containing mandatory properties for the tracking method.
         * @param {ExperienceModel} payload.experience The `ExperienceModel` instance.
         * @param {String} payload.triggerOrigin The `triggerOrigin` used for tracking.
         */
        _trackVariant(eventName, {experience, triggerOrigin}) {
            const eventProperties = {
                projectId: this._project && this._project.get("id"),
                groupId: this._group.get("id"),
                groupType: this._group.get("type"),
                groupVersion: this._group.get("version"),
                groupPushedVersion: this._group.get("pushedVersion"),
                triggerOrigin,
                experienceId: experience.get("id"),
                experienceVersion: experience.get("version"),
                experiencePushedVersion: experience.get("pushedVersion")
            };

            EditorEvents.trigger(eventName, eventProperties);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/actions/AddBreakpointAction.js